import React, { useEffect, useState } from 'react';
import { Typography, Chip } from '@mui/material';
import { useLocale } from '../../../i18n/LanguageContext';

const WHEN_TO_PAY_OPTIONS = [
  { key: 'atPickup', labelKey: 'payAtPickup' },
  { key: 'upfront', labelKey: 'upfront' },
];

const HOW_TO_PAY_OPTIONS = [
  { key: 'cash', labelKey: 'cash' },
  { key: 'payment_apps', labelKey: 'paymentApps' },
];

function parsePaymentMethods(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return [];
  }

  return value
    .split(',')
    .map((method) => method.trim())
    .map((method) => (method === 'paymentApps' ? 'payment_apps' : method))
    .filter(Boolean);
}

function toPaymentMethodsValue(methods) {
  if (!Array.isArray(methods) || methods.length === 0) {
    return '';
  }

  return methods.join(',');
}

export default function KitchenPaymentPreferences(props) {
  const { t } = useLocale();
  const [selectedWhenToPay, setSelectedWhenToPay] = useState(props.kitchenData?.paymentPreference || 'atPickup');
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState(
    parsePaymentMethods(props.kitchenData?.paymentMethod)
  );

  function getKitchenDataWithoutLegacyPaymentMethods() {
    const { paymentMethods, ...kitchenDataWithoutLegacy } = props.kitchenData || {};
    return kitchenDataWithoutLegacy;
  }

  useEffect(() => {
    setSelectedWhenToPay(props.kitchenData?.paymentPreference || 'atPickup');
  }, [props.kitchenData?.paymentPreference]);

  useEffect(() => {
    const nextMethods = parsePaymentMethods(props.kitchenData?.paymentMethod);

    if ((props.kitchenData?.paymentPreference || 'atPickup') === 'upfront') {
      setSelectedPaymentMethods(['payment_apps']);
      return;
    }

    setSelectedPaymentMethods(nextMethods);
  }, [props.kitchenData?.paymentMethod, props.kitchenData?.paymentPreference]);

  useEffect(() => {
    if (!props.kitchenData?.paymentPreference) {
      props.onDataChange({
        ...getKitchenDataWithoutLegacyPaymentMethods(),
        paymentPreference: 'atPickup',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function selectWhenToPay(optionKey) {
    setSelectedWhenToPay(optionKey);

    if (optionKey === 'upfront') {
      const enforcedMethods = ['payment_apps'];
      setSelectedPaymentMethods(enforcedMethods);
      props.onDataChange({
        ...getKitchenDataWithoutLegacyPaymentMethods(),
        paymentPreference: optionKey,
        paymentMethod: toPaymentMethodsValue(enforcedMethods),
      });
      return;
    }

    props.onDataChange({
      ...getKitchenDataWithoutLegacyPaymentMethods(),
      paymentPreference: optionKey,
    });
  }

  function togglePaymentMethod(optionKey) {
    if (selectedWhenToPay === 'upfront') {
      return;
    }

    const nextMethods = selectedPaymentMethods.includes(optionKey)
      ? selectedPaymentMethods.filter((method) => method !== optionKey)
      : [...selectedPaymentMethods, optionKey];
    const paymentMethodsValue = toPaymentMethodsValue(nextMethods);

    setSelectedPaymentMethods(nextMethods);
    props.onDataChange({
      ...getKitchenDataWithoutLegacyPaymentMethods(),
      paymentMethod: paymentMethodsValue,
    });
  }

  return (
    <div>
      <Typography variant="h5">{t('kitchenPaymentPreferences.title')}</Typography>
      <div style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 280px' }}>
          <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
            {t('kitchenPaymentPreferences.whenToPay')}
          </Typography>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {WHEN_TO_PAY_OPTIONS.map((opt) => (
              <Chip
                key={opt.key}
                label={t(`kitchenPaymentPreferences.${opt.labelKey}`)}
                clickable
                color={selectedWhenToPay === opt.key ? 'primary' : 'default'}
                variant={selectedWhenToPay === opt.key ? 'filled' : 'outlined'}
                onClick={() => selectWhenToPay(opt.key)}
                data-testid={`payment-when-${opt.key}`}
              />
            ))}
          </div>
        </div>

        <div style={{ flex: '1 1 280px' }}>
          <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
            {t('kitchenPaymentPreferences.howToPay')}
          </Typography>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {HOW_TO_PAY_OPTIONS.map((opt) => (
              (() => {
                const isCashUnavailable = selectedWhenToPay === 'upfront' && opt.key === 'cash';

                return (
              <Chip
                key={opt.key}
                label={t(`kitchenPaymentPreferences.${opt.labelKey}`)}
                clickable={!isCashUnavailable}
                disabled={isCashUnavailable}
                color={selectedPaymentMethods.includes(opt.key) ? 'primary' : 'default'}
                variant={selectedPaymentMethods.includes(opt.key) ? 'filled' : 'outlined'}
                onClick={() => togglePaymentMethod(opt.key)}
                data-testid={`payment-method-${opt.key}`}
              />
                );
              })()
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
